import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  Award,
  FileText,
  Building2,
  Globe,
  CheckCircle,
  ChevronRight,
  Download,
  BadgeCheck,
} from "lucide-react";

const CertificationsPage = () => {
  const certifications = [
    {
      icon: Shield,
      title: "Veteran-Owned Small Business",
      status: "Active",
      description: "Certified veteran-owned business eligible for VOSB set-aside contracts and veteran preference programs.",
    },
    {
      icon: Globe,
      title: "SAM.gov Registered",
      status: "Active",
      description: "Active registration in the System for Award Management for federal contracting eligibility.",
    },
    {
      icon: Building2,
      title: "HUBZone Aligned",
      status: "Active",
      description: "Operations aligned with Historically Underutilized Business Zones for HUBZone contract eligibility.",
    },
    {
      icon: FileText,
      title: "CAGE Code Active",
      status: "Active",
      description: "Active Commercial and Government Entity code for defense and government contracting.",
    },
    {
      icon: Award,
      title: "UEI Active",
      status: "Active",
      description: "Active Unique Entity Identifier for government transactions and contract awards.",
    },
    {
      icon: BadgeCheck,
      title: "Fully Insured",
      status: "Active",
      description: "Comprehensive insurance coverage meeting all government contractor requirements.",
    },
  ];

  const naicsCodes = [
    { code: "481112", description: "Scheduled Freight Air Transportation" },
    { code: "481211", description: "Nonscheduled Chartered Freight Air Transportation" },
    { code: "489510", description: "Other Support Activities for Air Transportation" },
    { code: "541614", description: "Process, Physical Distribution, and Logistics Consulting Services" },
    { code: "561210", description: "Facilities Support Services" },
  ];

  return (
    <div className="min-h-screen" data-testid="certifications-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a365d]" data-testid="certifications-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
                COMPLIANCE
              </p>
              <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
                CERTIFICATIONS & STATUS
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed mb-8">
                AMR Global Holdings LLC maintains full government contractor compliance 
                and is positioned to support federal, state, and municipal contracts. 
                All certifications and registrations are current and active.
              </p>
              <Link to="/contact?type=capability_statement">
                <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="cert-capability-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Request Capability Statement
                </Button>
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm border border-white/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/omrsi8tb_1000028432.jpg"
                  alt="AMR Global Holdings Logo"
                  className="w-full max-w-xs mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="py-20 bg-white" data-testid="certifications-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">STATUS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">
              ALL CERTIFICATIONS
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="card-tactical relative overflow-hidden" data-testid={`certification-card-${index}`}>
                <CardContent className="p-8">
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold tracking-wider rounded-sm">
                      {cert.status}
                    </span>
                  </div>
                  <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                    <cert.icon className="w-7 h-7 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-3">{cert.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{cert.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* NAICS Codes */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="naics-codes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CODES</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">NAICS CODES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {naicsCodes.map((naics, index) => (
              <Card key={index} className="card-tactical" data-testid={`naics-code-${index}`}>
                <CardContent className="p-6">
                  <p className="font-mono text-3xl text-[#c5a04a] mb-3">{naics.code}</p>
                  <p className="text-slate-600 text-sm">{naics.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Details */}
      <section className="py-20 bg-white" data-testid="company-details">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CONTACT</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                COMPANY INFORMATION
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-[#c5a04a] mt-1" />
                  <div>
                    <p className="font-medium text-[#1a365d]">AMR Global Holdings LLC</p>
                    <p className="text-slate-600">6001 Stars and Stripes Blvd, Suite 244</p>
                    <p className="text-slate-600">New Orleans, LA 70126</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-[#c5a04a] mt-1" />
                  <div>
                    <p className="font-medium text-[#1a365d]">Headquarters</p>
                    <p className="text-slate-600">New Orleans Lakefront Airport (KNEW)</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">LEADERSHIP</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                POINT OF CONTACT
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-[#c5a04a] mt-1" />
                  <div>
                    <p className="font-medium text-[#1a365d]">Alexander M. Reid</p>
                    <p className="text-slate-600">Chief Executive Officer</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-[#c5a04a] mt-1" />
                  <div>
                    <p className="font-medium text-[#1a365d]">Contact</p>
                    <p className="text-slate-600">AMREID@AMRGLOBALHOLDINGS.COM</p>
                    <p className="text-slate-600">504-319-7683</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a365d]" data-testid="certifications-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 text-[#c5a04a] mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            GOVERNMENT CONTRACTOR READY
          </h2>
          <p className="text-slate-200 text-lg mb-8">
            AMR Global Holdings LLC is fully compliant and ready to support your 
            government contracting requirements. Request our capability statement today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact?type=capability_statement">
              <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="cta-capability-btn">
                <FileText className="w-4 h-4 mr-2" />
                Request Capability Statement
              </Button>
            </Link>
            <Link to="/contact?type=contracting">
              <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="cta-inquiry-btn">
                Contracting Inquiry
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CertificationsPage;
